import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Settings as SettingsIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { updateConfig } from "@/lib/api";
import { STORE } from "@/constants/testIds";

const StatusBanner = ({ configured, automaticFulfillment }) => (
  <div className="space-y-2">
    <div
      data-testid={STORE.shopifyUrlStatus}
      className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium ${
        configured
          ? "border-[color:rgba(31,138,91,0.35)] bg-[color:rgba(31,138,91,0.08)] text-[#1F8A5B]"
          : "border-[color:rgba(183,121,31,0.35)] bg-[color:rgba(214,162,30,0.10)] text-[#B7791F]"
      }`}
    >
      {configured ? (
        <>
          <CheckCircle2 className="h-4 w-4" /> Checkout is connected. Buy buttons
          open your checkout link.
        </>
      ) : (
        <>
          <AlertCircle className="h-4 w-4" /> Checkout not connected yet. Buy
          buttons show an opening-soon notice.
        </>
      )}
    </div>
    <div
      className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium ${
        automaticFulfillment
          ? "border-[color:rgba(31,138,91,0.35)] bg-[color:rgba(31,138,91,0.08)] text-[#1F8A5B]"
          : "border-[color:rgba(183,121,31,0.35)] bg-[color:rgba(214,162,30,0.10)] text-[#B7791F]"
      }`}
    >
      {automaticFulfillment ? (
        <>
          <CheckCircle2 className="h-4 w-4" /> Automatic account access and buyer email are ready.
        </>
      ) : (
        <>
          <AlertCircle className="h-4 w-4" /> Automatic access delivery still needs
          its email and paid-order connection configured.
        </>
      )}
    </div>
  </div>
);

export const ShopifyUrlForm = () => {
  const { data: config } = useSiteConfig();
  const queryClient = useQueryClient();
  const shopify_checkout_url = config?.shopify_checkout_url;
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (shopify_checkout_url !== undefined) {
      setUrl(shopify_checkout_url || "");
    }
  }, [shopify_checkout_url]);

  const mutation = useMutation({
    mutationFn: (payload) => updateConfig(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(["site-config"], data);
      queryClient.invalidateQueries({ queryKey: ["site-config"] });
      toast.success("Settings saved", {
        description: data.configured
          ? "Checkout link is live. All Buy buttons now open your checkout provider."
          : "Checkout link cleared. Buy buttons show a pending notice.",
      });
    },
    onError: () =>
      toast.error("Could not save", { description: "Please try again." }),
  });

  const configured = config?.configured;

  return (
    <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-8">
      <StatusBanner
        configured={configured}
        automaticFulfillment={config?.automatic_fulfillment}
      />

      <div className="mt-6">
        <Label htmlFor="shopify-url" className="text-sm font-medium text-[var(--ibp-ink)]">
          Checkout URL
        </Label>
        <Input
          id="shopify-url"
          data-testid={STORE.shopifyUrlInput}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://your-checkout-link.example/cart/VARIANT_ID:1"
          className="mt-1.5 h-11 rounded-xl"
        />
        <p className="mt-2 text-xs leading-5 text-[color:rgba(11,18,32,0.6)]">
          Tip: A direct cart or checkout link like{" "}
          <code className="rounded bg-[var(--ibp-offwhite)] px-1.5 py-0.5">
            https://your-checkout-link.example/cart/VARIANT_ID:1
          </code>{" "}
          sends buyers straight to checkout. You can also paste a product page
          URL. Leave blank to disable checkout.
        </p>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          data-testid={STORE.shopifyUrlSave}
          onClick={() => mutation.mutate({ shopify_checkout_url: url })}
          disabled={mutation.isPending}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.18)] transition-colors hover:bg-[var(--ibp-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ibp-focus-ring)] focus-visible:ring-offset-2 disabled:opacity-60"
        >
          <SettingsIcon className="h-4 w-4" />
          {mutation.isPending ? "Saving…" : "Save checkout link"}
        </button>
        {configured && config?.shopify_checkout_url && (
          <a
            href={config.shopify_checkout_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline"
          >
            Test the link <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
};

export default ShopifyUrlForm;
